import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../features/auth/AuthProvider.js";
import { ApiClientError } from "../services/api-client.js";
import * as inventoryApi from "../services/inventory-api.js";
import type { Product } from "../types/api.js";

type FormState = {
  sku: string;
  name: string;
  description: string;
  unit: string;
  isActive: boolean;
};

const EMPTY_FORM: FormState = { sku: "", name: "", description: "", unit: "", isActive: true };

function isPlatformRole(role: string): boolean {
  return role === "superadmin" || role === "platform-support";
}

function canMutate(role: string): boolean {
  return isPlatformRole(role) || role === "tenant-admin";
}

function fromProduct(p: Product): FormState {
  return {
    sku: p.sku,
    name: p.name,
    description: p.description ?? "",
    unit: p.unit ?? "",
    isActive: p.isActive,
  };
}

export function ProductsPage(): JSX.Element {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [skuFilter, setSkuFilter] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formTenantId, setFormTenantId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const allowMutation = !!user && canMutate(user.role);
  const isPlatform = !!user && isPlatformRole(user.role);

  const tenantQuery = useMemo(() => {
    if (user?.tenantId) return { tenantId: user.tenantId };
    return {};
  }, [user?.tenantId]);

  const friendlyError = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiClientError) {
        if (e.status === 401) return t("errors.unauthorized");
        if (e.status === 403) return t("errors.forbidden");
        if (e.status === 404) return t("errors.notFound");
        if (e.status === 409) return e.message;
        if (e.status >= 400 && e.status < 500) return e.message || t("errors.badRequest");
        return e.message || t("errors.generic");
      }
      return t("errors.generic");
    },
    [t],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoadState("loading");
    setErrorMsg(null);
    try {
      const { products: rows } = await inventoryApi.fetchProducts(token, {
        ...tenantQuery,
        sku: skuFilter.trim() || undefined,
      });
      setProducts(rows);
      setLoadState("ok");
    } catch (e) {
      setLoadState("error");
      setErrorMsg(friendlyError(e));
    }
  }, [token, tenantQuery, skuFilter, friendlyError]);

  useEffect(() => {
    void load();
  }, [load]);

  const startCreate = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormTenantId("");
    setFormError(null);
    setShowCreate(true);
  }, []);

  const startEdit = useCallback((product: Product) => {
    setShowCreate(false);
    setEditingId(product.id);
    setForm(fromProduct(product));
    setFormTenantId("");
    setFormError(null);
  }, []);

  const cancelForm = useCallback(() => {
    setShowCreate(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormTenantId("");
    setFormError(null);
  }, []);

  const submit = useCallback(async () => {
    if (!token) return;
    setFormError(null);
    setSubmitting(true);
    try {
      if (editingId) {
        const payload: inventoryApi.ProductUpdateInput = {
          sku: form.sku.trim(),
          name: form.name.trim(),
          description: form.description.trim() || null,
          unit: form.unit.trim() || null,
          isActive: form.isActive,
        };
        const { product } = await inventoryApi.updateProductRequest(token, editingId, payload);
        setProducts((prev) => prev.map((p) => (p.id === editingId ? product : p)));
        cancelForm();
      } else {
        const payload: inventoryApi.ProductCreateInput = {
          sku: form.sku.trim(),
          name: form.name.trim(),
          description: form.description.trim() || null,
          unit: form.unit.trim() || null,
          isActive: form.isActive,
        };
        if (isPlatform && formTenantId.trim()) {
          payload.tenantId = formTenantId.trim();
        }
        const { product } = await inventoryApi.createProductRequest(token, payload);
        setProducts((prev) => [product, ...prev]);
        cancelForm();
      }
    } catch (e) {
      setFormError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  }, [token, editingId, form, formTenantId, isPlatform, friendlyError, cancelForm]);

  const handleDelete = useCallback(
    async (product: Product) => {
      if (!token) return;
      const message = t("common.confirmDelete", { name: product.sku });
      if (!window.confirm(message)) return;
      try {
        await inventoryApi.deleteProductRequest(token, product.id);
        setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, isActive: false } : p)));
      } catch (e) {
        setErrorMsg(friendlyError(e));
      }
    },
    [token, t, friendlyError],
  );

  return (
    <div className="products-page">
      <header className="page-header">
        <h1>{t("portal.products.title")}</h1>
        <p className="text-muted">{t("portal.products.subtitle")}</p>
      </header>

      <section className="card filters-card">
        <h2 className="section-title">{t("common.search")}</h2>
        <div className="filters-row">
          <label className="field-inline">
            <span>{t("portal.products.sku")}</span>
            <input
              type="text"
              value={skuFilter}
              onChange={(e) => setSkuFilter(e.target.value)}
              placeholder={t("portal.products.skuPlaceholder")}
            />
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void load()}
            disabled={loadState === "loading"}
          >
            {loadState === "loading" ? t("common.loading") : t("common.search")}
          </button>
          {allowMutation && !showCreate && editingId == null ? (
            <button type="button" className="btn btn-primary" onClick={startCreate}>
              {t("portal.products.new")}
            </button>
          ) : null}
        </div>
      </section>

      {errorMsg ? <p className="form-error">{errorMsg}</p> : null}

      {(showCreate || editingId != null) && allowMutation ? (
        <section className="card create-order-card">
          <h2 className="section-title">
            {editingId ? t("portal.products.edit") : t("portal.products.new")}
          </h2>
          {formError ? <p className="form-error">{formError}</p> : null}
          <div className="filters-row">
            <label className="field-inline field-grow">
              <span>{t("portal.products.sku")}</span>
              <input
                type="text"
                value={form.sku}
                onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                maxLength={64}
                required
              />
            </label>
            <label className="field-inline field-grow">
              <span>{t("portal.products.name")}</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={200}
                required
              />
            </label>
            <label className="field-inline field-grow">
              <span>{t("portal.products.unit")}</span>
              <input
                type="text"
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                placeholder="UN / KG / L"
                maxLength={32}
              />
            </label>
            <label className="field-inline">
              <span>{t("common.status")}</span>
              <select
                value={form.isActive ? "active" : "inactive"}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === "active" }))}
              >
                <option value="active">{t("common.active")}</option>
                <option value="inactive">{t("common.inactive")}</option>
              </select>
            </label>
          </div>
          <label className="field field-block">
            <span>{t("portal.products.description")}</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              maxLength={2000}
            />
          </label>
          {isPlatform && !editingId ? (
            <label className="field field-block">
              <span>
                {t("portal.products.tenantId")} <small className="text-muted">{t("portal.products.tenantIdHint")}</small>
              </span>
              <input
                type="text"
                value={formTenantId}
                onChange={(e) => setFormTenantId(e.target.value)}
                placeholder="uuid"
              />
            </label>
          ) : null}
          <div className="status-actions">
            <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={submitting}>
              {submitting ? t("common.loading") : t("common.save")}
            </button>
            <button type="button" className="btn" onClick={cancelForm} disabled={submitting}>
              {t("common.cancel")}
            </button>
          </div>
        </section>
      ) : null}

      {loadState === "loading" ? <p className="text-muted">{t("common.loading")}</p> : null}

      {loadState === "ok" ? (
        <section className="card table-section">
          <h2 className="section-title">
            {t("portal.products.list")} <span className="text-muted small">({products.length})</span>
          </h2>
          {products.length === 0 ? (
            <p className="text-muted empty-hint">{t("common.noResults")}</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("portal.products.sku")}</th>
                    <th>{t("portal.products.name")}</th>
                    <th>{t("portal.products.unit")}</th>
                    <th>{t("portal.products.description")}</th>
                    <th>{t("common.status")}</th>
                    {allowMutation ? <th>{t("common.actions")}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <code>{p.sku}</code>
                      </td>
                      <td>{p.name}</td>
                      <td>{p.unit ?? <span className="text-muted">—</span>}</td>
                      <td className="truncate" title={p.description ?? ""}>
                        {p.description ?? <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <span className={p.isActive ? "badge-active" : "badge-inactive"}>
                          {p.isActive ? t("common.active") : t("common.inactive")}
                        </span>
                      </td>
                      {allowMutation ? (
                        <td className="nowrap">
                          <button type="button" className="btn btn-sm" onClick={() => startEdit(p)}>
                            {t("common.edit")}
                          </button>{" "}
                          {p.isActive ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => void handleDelete(p)}
                            >
                              {t("common.delete")}
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
